import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Spline from "@splinetool/react-spline";
import {
  ArrowRight,
  BadgeCheck,
  Boxes,
  CheckCircle2,
  ChevronRight,
  Clock,
  ExternalLink,
  Gauge,
  Layers,
  ShieldCheck,
  Star,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

import { aternLogoSVG, c4CloudLogoSVG } from "@/assets/partnerLogos";

import { mock } from "@/mock";
import { projectsService } from "@/api/projectsService";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ProjectCardSkeleton } from "@/components/ui/ProjectCardSkeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { Reveal } from "@/components/site/SiteLayout";

function GlassCard({ className, children }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-primary/20 bg-black/40 backdrop-blur-md",
        "shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-[transform,box-shadow,border-color,background-color] duration-200",
        "hover:-translate-y-[2px] hover:border-primary/40 hover:bg-black/60 hover:shadow-[0_0_24px_rgba(0,194,255,0.32)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SectionTitle({ eyebrow, title, desc, align = "center" }) {

  return (
    <div className={cn("max-w-[820px]", align === "left" ? "text-center lg:text-left" : "mx-auto text-center")}>
      <div className="text-xs tracking-[0.22em] text-white/55">{eyebrow}</div>
      <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-4xl">
        {title}
      </h2>
      <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base md:text-lg">
        {desc}
      </p>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();

  const [caseStudies, setCaseStudies] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  useEffect(() => {
    let mounted = true;
    projectsService
      .getPublishedProjects()
      .then((data) => {
        if (mounted) {
          setCaseStudies(data || []);
          setLoadingProjects(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load projects for home:", err);
        if (mounted) {
          setCaseStudies([]);
          setLoadingProjects(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const featuredCaseStudies = useMemo(() => {
    const featured = caseStudies.filter((cs) => cs.is_featured);
    return featured.length > 0 ? featured : caseStudies.slice(0, 3);
  }, [caseStudies]);

  const topServices = useMemo(() => mock.services.slice(0, 6), []);

  const dynamicTestimonials = useMemo(() => {
    return caseStudies
      .filter((cs) => cs.testimonial_quote)
      .map((cs) => ({
        name: cs.testimonial_author || cs.client || cs.title,
        company: cs.title,
        quote: cs.testimonial_quote,
        rating: cs.testimonial_rating || 5,
      }));
  }, [caseStudies]);

  const testimonialLoop = useMemo(
    () => [...dynamicTestimonials, ...dynamicTestimonials, ...dynamicTestimonials],
    [dynamicTestimonials],
  );

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="w-full px-5 pb-8 pt-6 md:pb-16 md:pt-16 xl:px-10 2xl:px-14">
          <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <Reveal delay={0}>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-xs text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Development-first IT services for modern teams
                </div>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="mt-5 text-4xl font-semibold tracking-tight text-white md:text-5xl lg:text-[56px] lg:leading-[1.05]">
                 Design premium, lightning fast digital experiences with us.
                </h1>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
                 From web apps and mobile products to ERP systems and SaaS platforms, we engineer reliable, high performance solutions that scale as you grow.
                </p>
              </Reveal>
              <Reveal delay={220}>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                   onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                    className={cn(
                      "h-[52px] rounded-xl bg-primary px-6 text-black btn-glow",
                      "transition-[transform,box-shadow,background-color] duration-200",
                      "hover:bg-primary/90 hover:-translate-y-[1px]",
                    )}
                  >
                    Get a Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/services")}
                    className="h-[52px] rounded-xl border border-white/15 bg-white/[0.06] px-6 text-white hover:bg-white/10"
                  >
                    View Services
                  </Button>
                </div>
                <div className="mt-10 grid gap-3 sm:grid-cols-3">
                  {[{
                    icon: <Gauge className="h-4 w-4" />,
                    title: "Performance",
                    desc: "Built for speed and reliability",
                  }, {
                    icon: <Layers className="h-4 w-4" />,
                    title: "Scalability",
                    desc: "Designed with modularity at its core",
                  }, {
                    icon: <ShieldCheck className="h-4 w-4" />,
                    title: "Reliability",
                    desc: "Rigorous testing for confident releases",
                  }].map((p, idx) => (
                    <div
                      key={p.title}
                      className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-4 backdrop-blur-xl"
                    >
                      <div className="flex items-center gap-2 text-white">
                        <div className="grid h-8 w-8 place-items-center rounded-xl bg-black/60 border border-white/10">
                          <div className="text-primary">{p.icon}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium">{p.title}</div>
                          <div className="text-xs text-white/60">{p.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
            <Reveal delay={120} className="relative">
              <div className="relative mx-auto aspect-square w-full max-w-[520px]">
                <div className="absolute inset-[10px] overflow-hidden rounded-[22px]  bg-black/30 backdrop-blur-xl">
                  <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-black/55 to-black/80" />
                </div>
                <div className="pointer-events-none absolute -inset-10 -z-10 bg-[radial-gradient(closest-side,rgba(0,194,255,0.18),transparent_60%)]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      {/* Services overview */}
      <section className="w-full overflow-hidden px-5 py-8 md:py-16 xl:px-10 2xl:px-14">

        <SectionTitle
          eyebrow="SERVICES"
          title="Elite full-stack talent. None of the agency markup."
          desc="From initial prototype to global deployment, we build clean, modular systems engineered for long-term stability and effortless growth."
        />
      
       

        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Map service id to route */}
          {topServices.map((s, idx) => {
            const serviceRouteMap = {
              'web-dev': '/services/web-development',
              'mobile': '/services/mobile-development',
              'erp': '/services/erp-services',
              'saas': '/services/saas-development',
              'uiux': '/services/uiux-design',
              'cloud': '/services/cloud-devops',
              'support': '/services/maintenance-support',
              'mvp-rapid': '/services/mvp-rapid-prototyping',
              'ai-ml': '/services/ai-ml-strategy',
              'generative-ai': '/services/generative-ai-solutions',
            };
            const route = serviceRouteMap[s.id] || '/services';
            return (
              <Reveal key={s.id} delay={60 + idx * 60}>
                <GlassCard className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-lg font-semibold tracking-tight text-white">
                        {s.title}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-white/70">
                        {s.short}
                      </p>
                    </div>
                    <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-black/50">
                      <Boxes className="h-5 w-5 text-primary" />
                    </div>
                  </div>

                 

                  <div className="mt-6">
                    <Button
                      variant="secondary"
                      className="w-full rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10"
                      onClick={() => navigate(route)}
                    >
                      Explore details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>

      </section>

      <Separator className="mx-auto w-full bg-white/10" />

      {/* Why choose */}
      <section className="w-full overflow-hidden px-5 py-8 md:py-16 xl:px-10 2xl:px-14">
        <SectionTitle
          eyebrow="WHY SHIVEDA"
          title="Engineering longevity into every partnership"
          desc="We prioritize clarity, sustainable engineering, and predictable delivery—so you can plan with confidence."
        />

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {[{
              icon: <BadgeCheck className="h-5 w-5" />,
              title: "Outcome-driven scope",
              desc: "We translate goals into a plan with clear deliverables, timelines, and acceptance criteria.",
            }, {
              icon: <Layers className="h-5 w-5" />,
              title: "Design-to-code consistency",
              desc: "A reusable component system keeps UI consistent and iteration fast.",
            }, {
              icon: <ShieldCheck className="h-5 w-5" />,
              title: "Quality & reliability",
              desc: "QA, release checklists, and monitoring basics—before and after launch.",
            }].map((v, idx) => (
              <Reveal key={v.title} delay={80 + idx * 80}>
                <GlassCard className="p-7 h-full">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-black/55">
                      <div className="text-primary">{v.icon}</div>
                    </div>
                    <div className="text-lg font-semibold tracking-tight text-white">
                      {v.title}
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">
                    {v.desc}
                  </p>
                </GlassCard>
              </Reveal>
            ))}
        </div>
      </section>

      {/* Tech stack */}
      <section className="mx-auto max-w-[1200px] px-5 py-8 md:py-16" id="services-anchor">
        <SectionTitle
          eyebrow="TOOLING"
          title="A modern stack that stays maintainable"
          desc="We pick tools that keep your product fast today—and easy to evolve tomorrow."
        />

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {mock.techStack.map((g, idx) => (
            <Reveal key={g.group} delay={80 + idx * 70}>
              <GlassCard className="p-7">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-white">{g.group}</div>
                    <div className="mt-1 text-xs text-white/55">Reliable defaults, flexible choices.</div>
                  </div>
                  <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-black/55">
                    <Star className="h-4 w-4 text-primary" />
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {g.items.map((t) => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="rounded-full border-white/10 bg-white/[0.05] text-white/80 hover:bg-white/[0.05]"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Case studies */}
      <section className="w-full px-5 py-8 md:py-16 xl:px-10 2xl:px-14">
        <SectionTitle
          eyebrow="PROJECTS"
          title="Selected work—focused on clarity and results"
          desc="A few representative engagements that show how we approach problems and measure outcomes."
        />

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loadingProjects ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <ProjectCardSkeleton key={`home-skeleton-${idx}`} />
            ))
          ) : featuredCaseStudies.length > 0 ? (
            featuredCaseStudies.map((cs, idx) => (
              <Reveal key={cs.id} delay={80 + idx * 80}>
                <div className="group relative h-full">
                  {/* Glow effect on hover */}
                  <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-primary/20 via-primary/10 to-transparent opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />
                  
                  <GlassCard className="relative h-full p-6 transition-all duration-300 group-hover:border-primary/30">
                    <div className="flex flex-col h-full gap-5">
                      {/* Header */}
                      <div>
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                            <span className="text-xs font-medium text-primary">{cs.category}</span>
                          </div>
                          <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-transparent transition-colors duration-300 group-hover:border-primary/30 group-hover:bg-primary/10">
                            <Layers className="h-4 w-4 text-primary" />
                          </div>
                        </div>

                        {/* Title & Client */}
                        <div className="min-h-[3.75rem]">
                          <h3 className="text-xl font-bold text-white transition-colors duration-300 group-hover:text-primary">
                            {cs.title}
                          </h3>
                          {cs.client && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-white/60">
                              <Users className="h-3.5 w-3.5 text-primary/70" />
                              <span>Client: {cs.client}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Problem Card */}
                      <div className="rounded-xl border border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-transparent p-4">
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-orange-400" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-orange-400">Challenge</span>
                          </div>
                          {cs.problem.length > 115 && (
                            <button
                              type="button"
                              onClick={() => navigate(`/projects?project=${cs.id}`)}
                              className="text-[11px] font-medium text-orange-400 hover:text-orange-300 transition-colors cursor-pointer hover:underline inline-flex items-center gap-0.5"
                              title="View full challenge in project details"
                            >
                              <span>more</span>
                              <ChevronRight className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        <p
                          onClick={() => cs.problem.length > 115 && navigate(`/projects?project=${cs.id}`)}
                          className={cn(
                            "text-sm leading-relaxed text-white/70 line-clamp-3 h-[4.5rem] overflow-hidden",
                            cs.problem.length > 115 && "cursor-pointer hover:text-white/90 transition-colors"
                          )}
                        >
                          {cs.problem}
                        </p>
                      </div>

                      {/* Solution Card */}
                      <div className="rounded-xl border border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent p-4">
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-400" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-green-400">Solution</span>
                          </div>
                          {cs.Services.length > 115 && (
                            <button
                              type="button"
                              onClick={() => navigate(`/projects?project=${cs.id}`)}
                              className="text-[11px] font-medium text-green-400 hover:text-green-300 transition-colors cursor-pointer hover:underline inline-flex items-center gap-0.5"
                              title="View full solution in project details"
                            >
                              <span>more</span>
                              <ChevronRight className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        <p
                          onClick={() => cs.Services.length > 115 && navigate(`/projects?project=${cs.id}`)}
                          className={cn(
                            "text-sm leading-relaxed text-white/70 line-clamp-3 h-[4.5rem] overflow-hidden",
                            cs.Services.length > 115 && "cursor-pointer hover:text-white/90 transition-colors"
                          )}
                        >
                          {cs.Services}
                        </p>
                      </div>

                      {/* Tech Stack */}
                      <div>
                        <div className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-white/50">Tech Stack</div>
                        <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                          {cs.stack.slice(0, 3).map((t) => (
                            <Badge
                              key={t}
                              className="rounded-lg border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 whitespace-nowrap shrink-0"
                            >
                              {t}
                            </Badge>
                          ))}
                          {cs.stack.length > 3 && (
                            <button
                              type="button"
                              onClick={() => navigate(`/projects?project=${cs.id}`)}
                              className="rounded-lg border border-primary/40 bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/25 hover:border-primary/60 transition-colors whitespace-nowrap shrink-0 cursor-pointer"
                              title="View project details to see all technologies"
                            >
                              +{cs.stack.length - 3} more
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Outcome */}
                      {cs.outcome && (
                        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-primary/5 to-transparent p-4">
                          <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-primary" />
                              <span className="text-xs font-semibold uppercase tracking-wider text-primary">Impact</span>
                            </div>
                            {cs.outcome.length > 115 && (
                              <button
                                type="button"
                                onClick={() => navigate(`/projects?project=${cs.id}`)}
                                className="text-[11px] font-medium text-primary/90 hover:text-primary transition-colors cursor-pointer hover:underline inline-flex items-center gap-0.5"
                                title="View full outcome in project details"
                              >
                                <span>more</span>
                                <ChevronRight className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <p
                            onClick={() => cs.outcome.length > 115 && navigate(`/projects?project=${cs.id}`)}
                            className={cn(
                              "text-sm leading-relaxed text-white/70 line-clamp-3 h-[4.5rem] overflow-hidden",
                              cs.outcome.length > 115 && "cursor-pointer hover:text-white/90 transition-colors"
                            )}
                          >
                            {cs.outcome}
                          </p>
                        </div>
                      )}

                      {/* Status Badge */}
                      {cs.status && cs.status.toLowerCase() !== "completed" && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-yellow-400" />
                          <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/10">
                            {cs.status}
                          </Badge>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-auto pt-2 flex gap-2.5">
                        {cs.url && (
                          <Button
                            variant="secondary"
                            onClick={() => window.open(cs.url, "_blank")}
                            className="flex-1 rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10 hover:border-primary/30 transition-all duration-300 group/btn"
                          >
                            <span>View Live</span>
                            <ExternalLink className="ml-2 h-3.5 w-3.5 transition-transform group-hover/btn:scale-110" />
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/projects?project=${cs.id}`)}
                          className={cn(
                            "rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent text-white hover:from-primary/20 hover:to-primary/5 transition-all duration-300 group/btn",
                            cs.url ? 'flex-1' : 'w-full'
                          )}
                        >
                          <span>Case Study</span>
                          <ChevronRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" />
                        </Button>
                      </div>
                    </div>
                  </GlassCard>
                </div>
              </Reveal>
            ))
          ) : null}
        </div>
      </section>

      {/* Testimonials (Rendered dynamically from published database projects) */}
      {dynamicTestimonials.length > 0 && (
        <section className="w-full overflow-hidden px-5 py-8 mb-6 md:py-24 md:mb-12 xl:px-10 2xl:px-14">
        {/* Mobile and Tablet: Horizontal Scrolling */}
        <div className="block lg:hidden mb-10">
          <SectionTitle
            align="left"
            eyebrow="TRUST"
            title="Teams choose us for reliable delivery"
            desc="Clear communication, clean engineering, and a premium experience end-to-end. We map risk, share honest updates, and keep delivery predictable so your roadmap never slips."
          />
        </div>

        <Reveal delay={80}>
          <div className="block lg:hidden">
            <div className="testimonial-horizontal-wrapper relative h-[320px] w-full overflow-hidden">
              <div className="testimonial-loop-horizontal flex gap-4">
                {testimonialLoop.map((t, idx) => (
                  <Card
                    key={`${t.name}-horizontal-${idx}`}
                    className="min-w-[280px] max-w-[280px] h-[280px] flex-shrink-0 rounded-2xl border border-primary/20 bg-black/40 p-5 text-left shadow-[0_18px_50px_rgba(0,0,0,0.4)] backdrop-blur-md"
                  >
                    <CardHeader className="p-0">
                      <div className="flex items-center justify-between gap-3 text-xs text-white/65">
                        <div className="flex items-center gap-1 text-amber-300">
                          {Array.from({ length: 5 }).map((_, starIdx) => (
                            <Star
                              key={starIdx}
                              className="h-3.5 w-3.5 text-amber-300"
                              fill="currentColor"
                              strokeWidth={1.5}
                            />
                          ))}
                        </div>
                        <div className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] text-white/65">
                          {t.company}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="mt-4 text-sm leading-relaxed text-white/80 line-clamp-4">
                        "{t.quote}"
                      </p>
                      <div className="mt-5 flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary/70 via-white/20 to-black/70 text-sm font-semibold text-white">
                          {t.name.charAt(0)}
                        </div>
                        <div className="leading-tight">
                          <div className="text-sm font-medium text-white">{t.name}</div>
                          <div className="text-xs text-white/60">
                            @{t.name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 12) || "client"}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Desktop: Previous Layout */}
        <div className="hidden lg:grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div className="space-y-6 lg:max-w-[520px] lg:h-full lg:flex lg:flex-col lg:justify-center">
            <SectionTitle
              align="left"
              eyebrow="TRUST"
              title="Teams choose us for reliable delivery"
              desc="Clear communication, clean engineering, and a premium experience end-to-end. We map risk, share honest updates, and keep delivery predictable so your roadmap never slips."
            />
          </div>

          <Reveal delay={80}>
            <div className="mx-auto grid max-w-[1160px] gap-4 sm:grid-cols-2">
              <div className="marquee-wrapper relative h-[600px]">
                <div className="blur-overlay blur-top" />
                <div className="blur-overlay blur-bottom" />
                <div className="testimonial-loop testimonial-loop-up">
                  {testimonialLoop.map((t, idx) => (
                    <Card
                      key={`${t.name}-col1-${idx}`}
                      className="h-full rounded-2xl border border-primary/20 bg-black/40 p-5 text-left shadow-[0_18px_50px_rgba(0,0,0,0.4)] backdrop-blur-md"
                    >
                      <CardHeader className="p-0">
                        <div className="flex items-center justify-between gap-3 text-xs text-white/65">
                          <div className="flex items-center gap-1 text-amber-300">
                            {Array.from({ length: 5 }).map((_, starIdx) => (
                              <Star
                                key={starIdx}
                                className="h-3.5 w-3.5 text-amber-300"
                                fill="currentColor"
                                strokeWidth={1.5}
                              />
                            ))}
                          </div>
                          <div className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] text-white/65">
                            {t.company}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <p className="mt-4 text-sm leading-relaxed text-white/80">
                          “{t.quote}”
                        </p>
                        <div className="mt-5 flex items-center gap-3">
                          <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary/70 via-white/20 to-black/70 text-sm font-semibold text-white">
                            {t.name.charAt(0)}
                          </div>
                          <div className="leading-tight">
                            <div className="text-sm font-medium text-white">{t.name}</div>
                            <div className="text-xs text-white/60">
                              @{t.name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 12) || "client"}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="marquee-wrapper relative h-[600px]">
                <div className="blur-overlay blur-top" />
                <div className="blur-overlay blur-bottom" />
                <div className="testimonial-loop testimonial-loop-down">
                  {testimonialLoop.map((t, idx) => (
                    <Card
                      key={`${t.name}-col2-${idx}`}
                      className="h-full rounded-2xl border border-primary/20 bg-black/40 p-5 text-left shadow-[0_18px_50px_rgba(0,0,0,0.4)] backdrop-blur-md"
                    >
                      <CardHeader className="p-0">
                        <div className="flex items-center justify-between gap-3 text-xs text-white/65">
                          <div className="flex items-center gap-1 text-amber-300">
                            {Array.from({ length: 5 }).map((_, starIdx) => (
                              <Star
                                key={starIdx}
                                className="h-3.5 w-3.5 text-amber-300"
                                fill="currentColor"
                                strokeWidth={1.5}
                              />
                            ))}
                          </div>
                          <div className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] text-white/65">
                            {t.company}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <p className="mt-4 text-sm leading-relaxed text-white/80">
                          “{t.quote}”
                        </p>
                        <div className="mt-5 flex items-center gap-3">
                          <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary/70 via-white/20 to-black/70 text-sm font-semibold text-white">
                            {t.name.charAt(0)}
                          </div>
                          <div className="leading-tight">
                            <div className="text-sm font-medium text-white">{t.name}</div>
                            <div className="text-xs text-white/60">
                              @{t.name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 12) || "client"}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      )}

        {/* Partners  */}
        <section className="w-full px-5 py-8 md:py-16 xl:px-10 2xl:px-14">
          <SectionTitle
            eyebrow="OUR PARTNERS"
            title="Trusted by teams building for scale"
            desc="We collaborate with platform partners and product teams to deliver reliable systems and fast launches."
          />

          <div className="mt-8 flex justify-center">
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              {(mock.partners ?? [
                { name: "C4CLOUD", src: "https://www.c4cloud.in/img/c4cloud-logo.webp" },
                { name: "Aterno Consulting", src: "/aterno_logo.png" },
              ]).map((p, idx) => (
                <div
                  key={p.name || idx}
                  className="flex items-center justify-center h-40 w-64"
                >
                  <img
                    src={p.src}
                    alt={p.name}
                    className={
                      p.name === "C4CLOUD"
                        ? "max-h-52 max-w-[320px] object-contain"
                        : "max-h-24 max-w-[180px] md:max-h-36 md:max-w-[240px] object-contain"
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

      {/* FAQ + CTA */}
      <section className="w-full px-5 py-8 md:py-16 xl:px-10 2xl:px-14">
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr]">
          <div>
            <div className="text-xs tracking-[0.22em] text-white/55">FAQ</div>
            <h3 className="mt-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
              Straight answers so you can move fast
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/70">
              A few of the most common questions we get from founders and teams.
            </p>

            <div className="mt-6">
              <Accordion type="single" collapsible className="w-full">
                {mock.faqs.map((f) => (
                  <AccordionItem
                    key={f.q}
                    value={f.q}
                    className="border-white/10"
                  >
                    <AccordionTrigger className="text-white/90 hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/70">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          <Reveal delay={120}>
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] p-8 backdrop-blur-[18px]">
              <div className="pointer-events-none absolute -top-24 right-[-90px] h-[280px] w-[280px] rounded-full bg-[radial-gradient(closest-side,rgba(0,194,255,0.20),transparent_65%)]" />
              <div className="pointer-events-none absolute -bottom-32 left-[-120px] h-[340px] w-[340px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.10),transparent_65%)]" />

              <div className="relative">
                <div className="text-xs tracking-[0.22em] text-white/55">CTA</div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  Ready to scope your project?
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/70">
                  Share your brief and we’ll respond with the next steps, timelines, and a proposal.
                </p>

                <div className="mt-7 grid gap-3">
                  <Button
                    //onClick={() => navigate("/contact#form")}
                    onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                    className="h-[52px] rounded-xl bg-primary text-black btn-glow hover:bg-primary/90"
                  >
                    Get a Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/projects")}
                    className="h-[52px] rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10"
                  >
                    View case studies
                  </Button>
                </div>

                <div className="mt-8 grid gap-3">
                  {["Clear scope", "Fast iteration", "Maintainable code"].map((p) => (
                    <div key={p} className="flex items-center gap-2 text-sm text-white/75">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      {p}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
